import { createFileRoute } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { BrandSeal } from "@/components/brand/BrandSeal";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { ShieldCheck, BookOpen, Trees, Check, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { isLevel } from "@/config/compliance";
import { ConsultCTA } from "@/components/brand/ConsultCTA";
import familyCoupleKids from "@/assets/family-couple-kids.jpg";
import familyCoupleElders from "@/assets/family-couple-elders.jpg";
import familyTrustFundBaby from "@/assets/family-trust-fund-baby.jpg";

export const Route = createFileRoute("/")({
  head: () =>
    seo({
      title: "Currency Thoughts — A Family Wealth Strategy Firm",
      description:
        "A Family Wealth Strategy Firm. Veteran-led Family Wealth Strategists and Economic Empowerment Coaches helping families build generational wealth.",
      canonical: "/",
      ogImage: "https://currencythoughts.net/og-seal.png",
    }),
  component: Index,
});

function Index({
  substantiatedFamilyCount,
}: {
  substantiatedFamilyCount?: string;
} = {}) {
  return (
    <div className="min-h-screen flex flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1">
        <section className="bg-navy-900 text-cream-50">
          <FiligreeFrame intensity="subtle">
            {/* Hero */}
            <div className="mx-auto max-w-7xl px-6 lg:px-12 pt-28 md:pt-32 pb-16 md:pb-20 min-h-[85vh] flex items-center">
              <div className="grid w-full grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 lg:gap-10 items-center">
                {/* Left */}
                <div>
                  <Eyebrow color="cream">The Family Capital System</Eyebrow>
                  <h1
                    className="mt-8 font-display font-bold text-cream-50"
                    style={{
                      fontSize: "clamp(2.25rem, 5.4vw, 4rem)",
                      lineHeight: 1.15,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Most families lose their wealth in three generations.{" "}
                    <em className="text-bronze-400 italic">
                      We teach the system that stops it.
                    </em>
                  </h1>
                  <p
                    className="mt-8 font-sans text-cream-50/85"
                    style={{ fontSize: "17px", lineHeight: 1.65, maxWidth: "480px" }}
                  >
                    Currency Thoughts is a veteran-led family wealth strategy firm. We teach
                    families how to build control, liquidity, and continuity into their money —
                    so what you build actually gets passed down.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-3">
                    {isLevel("full") ? (
                      <ConsultCTA />
                    ) : (
                      <PrimaryButton size="lg" asChild>
                        <Link to="/family-capital-system">Learn the System</Link>
                      </PrimaryButton>
                    )}
                    {isLevel("education_only") && (
                      <SecondaryButton size="lg" asChild>
                        <Link to="/academy/library">Read the Family Library</Link>
                      </SecondaryButton>
                    )}
                    {!isLevel("education_only") && (
                      <SecondaryButton size="lg" asChild>
                        <Link to="/academy/library">Read the Family Library</Link>
                      </SecondaryButton>
                    )}
                  </div>
                </div>

                {/* Right — Thought bubbles + Seal composition */}
                <ThoughtBubbleComposition />
              </div>
            </div>

            {/* Stats strip */}
            {/* COMPLIANCE: Numeric client-count claims require a documented */}
            {/* substantiation file under GA advertising rules. Do not restore */}
            {/* "300+" without one on record. */}
            <div className="mx-auto max-w-7xl px-6 lg:px-12 pb-20">
              <div className="h-px w-full bg-bronze-600/40" aria-hidden />
              <div className="pt-10 flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16">
                <Stat value="EST. 2021" label="Founded" />
                <span className="hidden md:block h-10 w-px bg-bronze-600/30" aria-hidden />
                {substantiatedFamilyCount ? (
                  <Stat value={substantiatedFamilyCount} label="Families Served" />
                ) : (
                  <Stat value="Veteran" label="Owned & Operated" />
                )}
                <span className="hidden md:block h-10 w-px bg-bronze-600/30" aria-hidden />
                <Stat value="100%" label="Veteran Led" />
              </div>
            </div>
          </FiligreeFrame>
        </section>

        {/* SECTION 3 — Three Pillars */}
        <section className="bg-cream-50 py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="flex flex-col items-center text-center">
              <Eyebrow color="bronze">Our Practice Pillars</Eyebrow>
              <h2
                className="mt-6 font-display font-semibold text-navy-800"
                style={{ fontSize: "40px", letterSpacing: "-0.01em", lineHeight: 1.15 }}
              >
                How we serve.{" "}
                <em className="text-bronze-600 italic font-semibold">One legacy.</em>
              </h2>
            </div>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4">
              <PillarCard
                icon={ShieldCheck}
                principle="Protection"
                title="Control"
                body="Wealth you don't control isn't wealth — it's exposure. We teach families how protection is structured so a job loss, an illness, or a death doesn't unwind a decade of progress."
                to="/solutions"
                hash="family-protection"
              />
              <PillarCard
                icon={BookOpen}
                principle="Education"
                title="Liquidity"
                body="Money locked away until 59½ can't help you at 41. We teach how cash value, policy loans, and the velocity of money actually work — the principles behind what's often called infinite banking — so you understand the mechanics before anyone shows you a product."
                to="/family-capital-system"
              />
              <PillarCard
                icon={Trees}
                principle="Legacy"
                title="Continuity"
                body="A policy is not a plan. We teach how trust structure, ownership, and beneficiary design work together so wealth transfers with intention instead of leaking through probate and taxes."
                to="/solutions"
                hash="generational-wealth"
              />
            </div>
          </div>
        </section>

        {/* SECTION 4 — Why Currency Thoughts */}
        <section className="bg-navy-800 text-cream-50 py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-center">
              <div>
                <Eyebrow color="bronze">What Makes Us Different</Eyebrow>
                <h2
                  className="mt-6 font-display font-semibold text-cream-50"
                  style={{ fontSize: "32px", lineHeight: 1.2, letterSpacing: "-0.01em" }}
                >
                  Why families{" "}
                  <em className="text-bronze-400 italic font-semibold">choose us.</em>
                </h2>
                <p
                  className="mt-6 font-sans text-cream-50/80"
                  style={{ fontSize: "14px", lineHeight: 1.7 }}
                >
                  We are an independent, veteran-led family wealth strategy firm. We teach the
                  principles first, and we treat every household like our own.
                </p>
              </div>

              <ul className="flex flex-col gap-5">
                <Differentiator
                  bold="Veteran-led"
                  rest=" — discipline, mission focus, family first."
                />
                <Differentiator
                  bold="We teach before we transact"
                  rest=" — informed families make durable decisions."
                />
                <Differentiator
                  bold="Independent"
                  rest=" — we are not captive to a single product story."
                />
                <Differentiator
                  bold="Built for the whole household"
                  rest=" — parents, kids, and the generation after."
                />
                <Differentiator
                  bold="Secure client portal and document vault"
                  rest=" — your family's records, organized and protected."
                />
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 5 — Recruiting strip */}
        <section className="bg-navy-700 py-16 border-y border-bronze-600">
          <div className="mx-auto max-w-[640px] px-6 text-center flex flex-col items-center">
            <Eyebrow color="bronze">Become a Family Wealth Strategist</Eyebrow>
            <h2
              className="mt-6 font-display font-semibold text-cream-50"
              style={{ fontSize: "28px", lineHeight: 1.25, letterSpacing: "-0.01em" }}
            >
              Build your book on{" "}
              <em className="text-bronze-400 italic font-semibold">modern rails.</em>
            </h2>
            <p
              className="mt-4 font-sans text-cream-50/80"
              style={{ fontSize: "13px", lineHeight: 1.65 }}
            >
              Training included. Mentorship included. Legacy mindset required.
            </p>
            <div className="mt-8">
              <PrimaryButton size="lg" asChild>
                <Link to="/careers">Explore the Opportunity →</Link>
              </PrimaryButton>
            </div>
          </div>
        </section>

        {/* SECTION 6 — Final CTA */}
        <section className="bg-cream-50 py-24">
          <div className="mx-auto max-w-3xl px-6 text-center flex flex-col items-center">
            <Eyebrow color="bronze">One Conversation</Eyebrow>
            <h2
              className="mt-6 font-display font-semibold text-navy-800 max-w-[600px]"
              style={{ fontSize: "36px", lineHeight: 1.2, letterSpacing: "-0.01em" }}
            >
              Your family's legacy starts with{" "}
              <em className="text-bronze-600 italic font-semibold">one conversation.</em>
            </h2>
            <p
              className="mt-4 font-sans text-charcoal-500 max-w-[480px]"
              style={{ fontSize: "16px", lineHeight: 1.65 }}
            >
              No pressure. No sales pitch. Start with the principles your family's plan is built
              on.
            </p>
            <div className="mt-8">
              {isLevel("full") && (
                <PrimaryButton size="lg" asChild>
                  <Link to="/family-capital-system">Learn the System</Link>
                </PrimaryButton>
              )}
              {isLevel("education_only") && (
                <PrimaryButton size="lg" asChild>
                  <Link to="/family-capital-system">Learn the System</Link>
                </PrimaryButton>
              )}
              {!isLevel("education_only") && <ConsultCTA />}
            </div>
            {isLevel("full") && (
            <div
              className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-charcoal-500 font-sans font-semibold uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.15em" }}
            >
              <span>30-min Call</span>
              <span className="w-1 h-1 rounded-full bg-bronze-600" aria-hidden />
              <span>Free of Charge</span>
              <span className="w-1 h-1 rounded-full bg-bronze-600" aria-hidden />
              <span>No Pressure</span>
            </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function PillarCard({
  icon: Icon,
  principle,
  title,
  body,
  to,
  hash,
}: {
  icon: LucideIcon;
  principle: string;
  title: string;
  body: string;
  to: "/solutions" | "/family-capital-system";
  hash?: string;
}) {
  return (
    <article
      className="group flex flex-col items-center text-center bg-card border-t-[3px] border-bronze-600 px-5 py-6 transition-shadow duration-300 hover:shadow-[0_18px_40px_-18px_rgba(168,139,92,0.45)]"
      style={{ borderRadius: "var(--radius-card)" }}
    >
      <span className="flex items-center justify-center w-[46px] h-[46px] rounded-full bg-navy-800">
        <Icon size={22} className="text-bronze-400" strokeWidth={1.75} />
      </span>
      <span
        className="mt-4 font-sans font-semibold uppercase text-bronze-600"
        style={{ fontSize: "10px", letterSpacing: "0.22em" }}
      >
        {principle}
      </span>
      <h3
        className="mt-2 font-display font-semibold text-navy-800"
        style={{ fontSize: "18px", letterSpacing: "-0.01em" }}
      >
        {title}
      </h3>
      <p
        className="mt-3 font-sans text-charcoal-500"
        style={{ fontSize: "13px", lineHeight: 1.6 }}
      >
        {body}
      </p>
      <Link
        to={to}
        hash={hash}
        className="mt-6 inline-flex items-center gap-1.5 font-sans font-semibold uppercase text-bronze-600 hover:text-bronze-400 transition-colors"
        style={{ fontSize: "11px", letterSpacing: "0.05em" }}
      >
        Learn more <ArrowRight size={12} strokeWidth={2} />
      </Link>
    </article>
  );
}

function Differentiator({ bold, rest }: { bold: string; rest: string }) {
  return (
    <li className="flex items-start gap-2.5" style={{ fontSize: "13px", lineHeight: 1.7 }}>
      <Check
        size={16}
        strokeWidth={2.25}
        className="text-bronze-400 mt-[3px] shrink-0"
        aria-hidden
      />
      <span className="text-cream-50/85">
        <strong className="text-bronze-400 font-semibold">{bold}</strong>
        {rest}
      </span>
    </li>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span
        className="font-display font-bold text-bronze-400"
        style={{ fontSize: "24px", letterSpacing: "0.02em" }}
      >
        {value}
      </span>
      <span className="mt-3 font-sans text-[10px] font-semibold uppercase tracking-[0.22em] text-cream-50/70">
        {label}
      </span>
    </div>
  );
}

/* ------- Thought bubble composition ------- */

function ThoughtBubbleComposition() {
  return (
    <>
      {/* Mobile: seal only, centered */}
      <div className="flex md:hidden justify-center">
        <BrandSeal size={140} glow />
      </div>

      {/* Tablet & desktop: 3 thinking bubbles + seal */}
      <div className="relative mx-auto hidden md:block w-full max-w-[720px] min-h-[560px] lg:min-h-[680px]">
        {/* TODO: Replace with real photo — Young couple with their children */}
        <Bubble
          src={familyCoupleKids}
          alt="A young couple with their children"
          style={{ top: "4%", left: "8%" }}
          floatClass="bubble-float-1"
          objectPosition="50% 30%"
          priority
        />
        {/* TODO: Replace with real photo — Multi-generational couple with their parents */}
        <Bubble
          src={familyCoupleElders}
          alt="A multi-generational family — a couple with their parents"
          style={{ top: "12%", left: "52%" }}
          floatClass="bubble-float-2"
          objectPosition="50% 32%"
        />
        {/* TODO: Replace with real photo — Newborn in a wicker basket with Trust Fund Baby sign */}
        <Bubble
          src={familyTrustFundBaby}
          alt="A newborn baby in a basket with a Trust Fund Baby sign"
          style={{ top: "48%", left: "4%" }}
          floatClass="bubble-float-3"
          objectPosition="55% 50%"
          ribbon="Trust Fund Baby"
        />

        {/* Brand Seal — bottom-right anchor */}
        <div className="absolute" style={{ bottom: "4%", right: "4%" }}>
          <div className="lg:hidden">
            <BrandSeal size={180} glow />
          </div>
          <div className="hidden lg:block">
            <BrandSeal size={220} glow />
          </div>
        </div>
      </div>
    </>
  );
}

function Bubble({
  src,
  alt,
  style,
  floatClass,
  objectPosition = "50% 50%",
  priority,
  ribbon,
}: {
  src: string;
  alt: string;
  style: React.CSSProperties;
  floatClass: string;
  objectPosition?: string;
  priority?: boolean;
  ribbon?: string;
}) {
  return (
    <div
      className={`absolute ${floatClass}`}
      style={{
        ...style,
        width: "clamp(200px, 22vw, 240px)",
        height: "clamp(160px, 17.5vw, 190px)",
      }}
    >
      <div className="relative h-full w-full">
        {/* Bubble */}
        <div
          className="relative h-full w-full overflow-hidden bg-cream-50"
          style={{
            borderRadius: "50% / 48%",
            border: "4px solid var(--color-bronze-600)",
            boxShadow:
              "inset 0 0 0 2px var(--color-bronze-400), 0 22px 50px -22px rgba(0,0,0,0.55)",
          }}
        >
          <img
            src={src}
            alt={alt}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding={priority ? "sync" : "async"}
            width={1024}
            height={1024}
            className="h-full w-full object-cover"
            style={{ objectPosition, borderRadius: "50% / 48%" }}
          />
          {ribbon ? (
            <span
              className="absolute bg-bronze-400 text-navy-800 font-sans font-semibold uppercase"
              style={{
                bottom: 12,
                right: 12,
                width: 110,
                height: 26,
                fontSize: 9,
                letterSpacing: "0.12em",
                lineHeight: "26px",
                textAlign: "center",
                transform: "rotate(-12deg)",
                outline: "1px solid var(--color-bronze-600)",
                boxShadow: "0 4px 10px -4px rgba(0,0,0,0.4)",
              }}
            >
              {ribbon}
            </span>
          ) : null}
        </div>

        {/* Trail dots — pointing toward seal (bottom-right) */}
        <div
          className="absolute pointer-events-none"
          style={{ right: -24, bottom: -18, width: 60, height: 40 }}
          aria-hidden
        >
          {[16, 12, 8].map((size, i) => (
            <span
              key={i}
              className="absolute block rounded-full bg-cream-50"
              style={{
                width: size,
                height: size,
                right: i * 14,
                bottom: i * 10,
                border: "2px solid var(--color-bronze-600)",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}


